import React, { useState, useCallback } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import { BookOpenCheck, Pencil, Check, Variable, BookOpen, Send, Repeat, Zap, MessageSquare, Hash, History, LogOut, Globe, UserCircle, Tags, Plus, Trash2 } from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";
import { getEditorContainerProps } from "./nodeEditorLayout";

// ─── Collapsed View ───────────────────────────────────
const INTENT_ROW_H = 20;

const CollapsedView = ({ data, onEdit, intents, showNoAnswerHandle }) => {
  const contextVariableNames = data.contextVariableNames || [];
  const sessionMode = data.sessionMode || false;
  const maxQuestions = data.maxQuestions || 10;
  const hasIntents = intents.length > 0;

  return (
    <div
      className="relative rounded-xl overflow-visible flex"
      style={{
        backgroundColor: "#EDE9FE",
        padding: "10px",
        minWidth: hasIntents ? "340px" : "260px",
        maxWidth: hasIntents ? "400px" : "320px",
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit smart assistant"
      >
        <Pencil size={12} className="text-purple-600" />
      </button>

      {/* Main config column */}
      <div className="bg-white rounded-lg shadow-sm p-3 flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-purple-100 rounded-lg">
            <BookOpenCheck size={14} className="text-purple-600" />
          </div>
          <span className="text-[12px] font-bold text-purple-900">AI Smart Assistant</span>
        </div>

        {hasIntents && (
          <p className="text-[9px] text-violet-600 font-medium mb-1.5">
            {intents.length} intent route{intents.length !== 1 ? "s" : ""} → see panel on right
          </p>
        )}

        {/* Mode indicator */}
        <div className="flex items-center gap-1.5 mb-1.5">
          {sessionMode ? (
            <>
              <Repeat size={10} className="text-purple-500" />
              <span className="text-[10px] font-semibold text-purple-700">
                Session · up to {maxQuestions} question{maxQuestions !== 1 ? "s" : ""}
              </span>
            </>
          ) : (
            <>
              <Zap size={10} className="text-blue-500" />
              <span className="text-[10px] font-semibold text-blue-700">Single shot</span>
            </>
          )}
        </div>

        {/* KB attachment status */}
        <div className="flex items-center gap-1.5 mb-1.5">
          <BookOpen size={10} className={data.knowledgeBaseNodeId ? "text-amber-500" : "text-gray-300"} />
          <span className={`text-[10px] font-medium ${data.knowledgeBaseNodeId ? "text-amber-700" : "text-gray-400"}`}>
            {data.knowledgeBaseNodeId ? "KB attached" : "No KB attached"}
          </span>
        </div>

        {/* Intro message preview (session mode only) */}
        {sessionMode && data.introMessage && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <MessageSquare size={10} className="text-purple-400" />
            <span className="text-[9px] text-gray-500 truncate max-w-[200px]">
              {data.introMessage.substring(0, 60)}{data.introMessage.length > 60 ? "..." : ""}
            </span>
          </div>
        )}

        {/* History context indicator */}
        {data.prePromptMessagesCount > 0 && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <History size={10} className="text-purple-400" />
            <span className="text-[10px] font-medium text-purple-600">
              Last {data.prePromptMessagesCount} messages as context
            </span>
          </div>
        )}

        {/* Exit on no answer indicator (session only) */}
        {sessionMode && data.exitOnNoAnswer && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <LogOut size={10} className="text-orange-400" />
            <span className="text-[10px] font-medium text-orange-600">
              Exit if no answer
            </span>
          </div>
        )}

        {/* Role indicator */}
        {data.assistantRole && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <UserCircle size={10} className="text-indigo-400" />
            <span className="text-[9px] text-indigo-700 font-medium truncate max-w-[200px]">
              {data.assistantRole.length > 40 ? data.assistantRole.substring(0, 40) + "..." : data.assistantRole}
            </span>
          </div>
        )}

        {/* Language indicator */}
        {data.answerLanguageMode && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <Globe size={10} className="text-teal-400" />
            <span className="text-[9px] text-teal-700 font-medium">
              {data.answerLanguageMode === "fixed_language" && data.answerLanguage
                ? `Answer in ${data.answerLanguage}`
                : "Answer in user's language"}
            </span>
          </div>
        )}

        {/* Context variables */}
        {contextVariableNames.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {contextVariableNames.map((vn, i) => (
              <span key={i} className="text-[9px] font-mono font-semibold text-[#008069] bg-[#E7FCE8] px-1.5 py-0.5 rounded">
                {`{{${vn}}}`}
              </span>
            ))}
          </div>
        )}

        {/* Actions summary */}
        <div className="flex items-center gap-2 mt-1">
          {data.sendReply && (
            <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5">
              <Send size={7} /> Send reply
            </span>
          )}
          {data.storeInVariable && (
            <span className="text-[9px] bg-[#E7FCE8] text-[#008069] px-1.5 py-0.5 rounded-full font-mono font-semibold">
              → {`{{${data.storeInVariable}}}`}
            </span>
          )}
        </div>
      </div>

      {/* Intent route rail — label + handle on same row (aligned dots) */}
      {hasIntents && (
        <div
          className="flex-shrink-0 self-stretch ml-1.5 pt-8 pb-2 pl-2 border-l border-violet-200/80 flex flex-col"
          style={{ width: "118px", minHeight: "100%" }}
        >
          <p className="text-[8px] font-bold text-violet-500 uppercase tracking-wide mb-1 pr-1">
            Routes
          </p>
          {intents.map((intent) => (
            <div
              key={intent.id}
              className="relative flex items-center pr-3"
              style={{ height: INTENT_ROW_H }}
            >
              <span
                className={`text-[9px] font-semibold leading-none truncate w-full ${
                  intent.connectedNodeId ? "text-green-700" : "text-violet-700"
                }`}
                title={intent.value}
              >
                {intent.value || intent.id}
              </span>
              <Handle
                type="source"
                position={Position.Right}
                id={`intent_${intent.id}`}
                className={`!absolute !w-3 !h-3 !border-2 !border-white !shadow-sm !-translate-y-1/2 ${
                  intent.connectedNodeId ? "!bg-green-500" : "!bg-violet-500"
                }`}
                style={{ right: -10, top: "50%" }}
                title={intent.value}
              />
            </div>
          ))}
          {showNoAnswerHandle && (
            <div className="relative flex items-center pr-3" style={{ height: INTENT_ROW_H }}>
              <span
                className="text-[9px] font-semibold text-orange-600 leading-none truncate w-full"
                title="When knowledge base cannot answer"
              >
                No answer
              </span>
              <Handle
                type="source"
                position={Position.Right}
                id="no_answer"
                className="!absolute !w-3 !h-3 !bg-orange-500 !border-2 !border-white !shadow-sm !-translate-y-1/2"
                style={{ right: -10, top: "50%" }}
                title="KB can't answer → route here"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Expanded Editor ──────────────────────────────────
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable, presentation = "inline" }) => {
  const shell = getEditorContainerProps(presentation, {
    backgroundColor: "#EDE9FE",
    borderRadius: "12px",
    padding: "14px",
    minWidth: "320px",
    maxWidth: "400px",
  });
  const fs = presentation === "fullscreen";
  const [contextVariableNames, setContextVariableNames] = useState(data.contextVariableNames || []);
  const [storeInVariable, setStoreInVariable] = useState(data.storeInVariable || "");
  const [sendReply, setSendReply] = useState(data.sendReply !== undefined ? data.sendReply : true);
  const [sessionMode, setSessionMode] = useState(data.sessionMode || false);
  const [maxQuestions, setMaxQuestions] = useState(data.maxQuestions || 10);
  const [introMessage, setIntroMessage] = useState(data.introMessage || "");
  const [prePromptMessagesCount, setPrePromptMessagesCount] = useState(data.prePromptMessagesCount || 0);
  const [exitOnNoAnswer, setExitOnNoAnswer] = useState(data.exitOnNoAnswer || false);
  const [assistantRole, setAssistantRole] = useState(data.assistantRole || "");
  const [answerLanguageMode, setAnswerLanguageMode] = useState(data.answerLanguageMode || "user_language");
  const [answerLanguage, setAnswerLanguage] = useState(data.answerLanguage || "");
  const [intents, setIntents] = useState(
    (data.intents || []).map((i) => ({ ...i, keywords: i.keywords || [] }))
  );
  const [intentPrompt, setIntentPrompt] = useState(data.intentPrompt || "");
  const [intentFallbackVariable, setIntentFallbackVariable] = useState(data.intentFallbackVariable || "");
  const [newIntentLabel, setNewIntentLabel] = useState("");
  const [newIntentKeywords, setNewIntentKeywords] = useState("");

  const buildSavePayload = () => ({
    contextVariableNames,
    storeInVariable,
    sendReply,
    sessionMode,
    maxQuestions,
    introMessage,
    prePromptMessagesCount,
    exitOnNoAnswer,
    assistantRole,
    answerLanguageMode,
    answerLanguage,
    intents,
    intentPrompt,
    intentFallbackVariable,
  });

  const addIntent = () => {
    const trimmed = newIntentLabel.trim();
    if (!trimmed) return;
    if (intents.some((i) => i.value === trimmed)) return;
    const keywords = newIntentKeywords
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);
    setIntents([
      ...intents,
      {
        id: `int_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
        value: trimmed,
        description: "",
        keywords,
        connectedNodeId: null,
      },
    ]);
    setNewIntentLabel("");
    setNewIntentKeywords("");
  };

  const removeIntent = (id) => setIntents(intents.filter((i) => i.id !== id));

  const updateIntent = (id, field, value) => {
    setIntents(intents.map((i) => (i.id === id ? { ...i, [field]: value } : i)));
  };

  const toggleVariable = (name) => {
    if (contextVariableNames.includes(name)) {
      setContextVariableNames(contextVariableNames.filter((v) => v !== name));
    } else {
      setContextVariableNames([...contextVariableNames, name]);
    }
  };

  const handleMaxQuestionsChange = (e) => {
    const val = parseInt(e.target.value, 10);
    setMaxQuestions(isNaN(val) || val < 1 ? 1 : val);
  };

  return (
    <div
      className={shell.className}
      style={shell.style}
      onClick={(e) => e.stopPropagation()}
    >
      {!shell.hideHeader && (
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-purple-200 rounded-lg">
            <BookOpenCheck size={14} className="text-purple-700" />
          </div>
          <span className="text-[13px] font-bold text-purple-900">AI Smart Assistant</span>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={() => onSave(buildSavePayload())}
            className="px-3 py-1.5 text-[11px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1 shadow-sm"
          >
            <Check size={12} /> Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
      )}

      <NodeEditorTabs nodeType="aiKnowledgeBaseAnswer">
      <div className={`bg-white rounded-xl shadow-sm space-y-4 ${fs ? "p-5" : "p-3 space-y-3"}`}>
        {/* KB attachment info */}
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
          <BookOpen size={12} className={data.knowledgeBaseNodeId ? "text-amber-600" : "text-gray-400"} />
          <span className={`text-[10px] font-medium ${data.knowledgeBaseNodeId ? "text-amber-700" : "text-gray-500"}`}>
            {data.knowledgeBaseNodeId
              ? "Knowledge Base node attached"
              : "Connect a Knowledge Base node to this node"}
          </span>
        </div>

        {/* Intent routes — answer FAQ OR start a connected flow */}
        <div className="p-2.5 rounded-lg bg-violet-50/60 border border-violet-100 space-y-2">
          <label className="flex items-center gap-1 text-[10px] font-bold text-violet-800 uppercase tracking-wider">
            <Tags size={10} /> Route by intent (optional)
          </label>
          <p className="text-[9px] text-gray-500">
            When the user wants to book, buy, or start a flow — route instantly. Otherwise answer from the knowledge base.
          </p>

          {intents.map((intent) => (
            <div key={intent.id} className="p-2 bg-white rounded-lg border border-violet-100 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={intent.value}
                  onChange={(e) => updateIntent(intent.id, "value", e.target.value)}
                  className="flex-1 px-2 py-1 text-[10px] font-semibold border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-400"
                  placeholder="Intent label e.g. Book Appointment"
                />
                <button
                  type="button"
                  onClick={() => removeIntent(intent.id)}
                  className="p-1 text-red-400 hover:text-red-600"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <input
                type="text"
                value={(intent.keywords || []).join(", ")}
                onChange={(e) =>
                  updateIntent(
                    intent.id,
                    "keywords",
                    e.target.value.split(",").map((k) => k.trim()).filter(Boolean)
                  )
                }
                className="w-full px-2 py-1 text-[9px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-400"
                placeholder="Fast keywords: book, appointment, visit"
              />
              <input
                type="text"
                value={intent.description || ""}
                onChange={(e) => updateIntent(intent.id, "description", e.target.value)}
                className="w-full px-2 py-1 text-[9px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-400"
                placeholder="Description for AI (optional)"
              />
            </div>
          ))}

          <div className="flex gap-1">
            <input
              type="text"
              value={newIntentLabel}
              onChange={(e) => setNewIntentLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addIntent()}
              className="flex-1 px-2 py-1.5 text-[10px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-400"
              placeholder="New intent e.g. Book Appointment"
            />
            <button
              type="button"
              onClick={addIntent}
              className="px-2 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
            >
              <Plus size={12} />
            </button>
          </div>
          <input
            type="text"
            value={newIntentKeywords}
            onChange={(e) => setNewIntentKeywords(e.target.value)}
            className="w-full px-2 py-1.5 text-[9px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-400"
            placeholder="Keywords for new intent (comma-separated)"
          />

          <textarea
            value={intentPrompt}
            onChange={(e) => setIntentPrompt(e.target.value)}
            className="w-full px-2 py-1.5 text-[10px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-400 resize-none"
            rows={2}
            placeholder="Extra routing instructions for AI (optional)"
          />
          <VariableDropdown
            value={intentFallbackVariable}
            onChange={setIntentFallbackVariable}
            flowVariables={flowVariables}
            allowCreate
            onCreateVariable={onCreateVariable}
            placeholder="Store detected intent in variable (optional)"
          />
        </div>

        {/* AI Role / Persona */}
        <div>
          <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">
            <UserCircle size={10} /> AI Role / Persona
          </label>
          <textarea
            value={assistantRole}
            onChange={(e) => setAssistantRole(e.target.value)}
            className="w-full px-2.5 py-1.5 text-[11px] font-medium border border-gray-200 rounded-lg focus:ring-1 focus:ring-purple-400 focus:border-purple-400 focus:outline-none resize-none"
            rows={2}
            placeholder="e.g. friendly support agent, legal advisor, sales assistant..."
          />
          <p className="text-[9px] text-gray-400 mt-0.5">
            Describe how the AI assistant should behave when answering questions.
          </p>
        </div>

        {/* Answer Language */}
        <div>
          <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">
            <Globe size={10} /> Answer Language
          </label>
          <div className="flex gap-1.5">
            <button
              onClick={() => setAnswerLanguageMode("user_language")}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-[10px] font-bold transition-colors border ${
                answerLanguageMode === "user_language"
                  ? "bg-teal-50 border-teal-300 text-teal-700"
                  : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
              }`}
            >
              User's Language
            </button>
            <button
              onClick={() => setAnswerLanguageMode("fixed_language")}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-[10px] font-bold transition-colors border ${
                answerLanguageMode === "fixed_language"
                  ? "bg-teal-50 border-teal-300 text-teal-700"
                  : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
              }`}
            >
              Fixed Language
            </button>
          </div>
          {answerLanguageMode === "fixed_language" && (
            <input
              type="text"
              value={answerLanguage}
              onChange={(e) => setAnswerLanguage(e.target.value)}
              className="w-full mt-1.5 px-2.5 py-1.5 text-[11px] font-semibold border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-400 focus:border-teal-400 focus:outline-none"
              placeholder="e.g. English, Hindi, Spanish..."
            />
          )}
          <p className="text-[9px] text-gray-400 mt-0.5">
            {answerLanguageMode === "user_language"
              ? "AI will detect the user's language and answer in the same language."
              : "AI will always answer in the specified language."}
          </p>
        </div>

        {/* Mode selector: Single shot vs Session */}
        <div>
          <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">
            Mode
          </label>
          <div className="flex gap-1.5">
            <button
              onClick={() => setSessionMode(false)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-[10px] font-bold transition-colors border ${
                !sessionMode
                  ? "bg-blue-50 border-blue-300 text-blue-700"
                  : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
              }`}
            >
              <Zap size={11} /> Single Shot
            </button>
            <button
              onClick={() => setSessionMode(true)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-[10px] font-bold transition-colors border ${
                sessionMode
                  ? "bg-purple-50 border-purple-300 text-purple-700"
                  : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
              }`}
            >
              <Repeat size={11} /> Session
            </button>
          </div>
          <p className="text-[9px] text-gray-400 mt-1">
            {sessionMode
              ? "Stay on this node and answer multiple questions from the KB until the limit is reached."
              : "Answer one question from the KB, then move to the next node."}
          </p>
        </div>

        {/* Session-only settings */}
        {sessionMode && (
          <div className="space-y-2.5 p-2.5 rounded-lg bg-purple-50/50 border border-purple-100">
            {/* Max questions */}
            <div>
              <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">
                <Hash size={10} /> Max Questions
              </label>
              <input
                type="number"
                min={1}
                value={maxQuestions}
                onChange={handleMaxQuestionsChange}
                className="w-full px-2.5 py-1.5 text-[11px] font-semibold border border-gray-200 rounded-lg focus:ring-1 focus:ring-purple-400 focus:border-purple-400 focus:outline-none"
                placeholder="10"
              />
              <p className="text-[9px] text-gray-400 mt-0.5">
                Move to next node after answering this many questions.
              </p>
            </div>

            {/* Intro message */}
            <div>
              <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">
                <MessageSquare size={10} /> Intro Message (optional)
              </label>
              <textarea
                value={introMessage}
                onChange={(e) => setIntroMessage(e.target.value)}
                className="w-full px-2.5 py-1.5 text-[11px] font-medium border border-gray-200 rounded-lg focus:ring-1 focus:ring-purple-400 focus:border-purple-400 focus:outline-none resize-none"
                rows={2}
                placeholder="You can ask me anything from the knowledge base (up to {{maxQuestions}} questions)."
              />
              <p className="text-[9px] text-gray-400 mt-0.5">
                Sent when user first reaches this node. Use {"{{maxQuestions}}"} for the limit.
              </p>
            </div>

            {/* Exit on No Answer */}
            <div className="flex items-center justify-between px-2 py-2 bg-orange-50 rounded-lg border border-orange-100">
              <span className="text-[10px] font-bold text-gray-600 flex items-center gap-1">
                <LogOut size={10} /> Exit session if no answer
              </span>
              <button
                onClick={() => setExitOnNoAnswer(!exitOnNoAnswer)}
                className={`w-9 h-5 rounded-full transition-colors relative ${exitOnNoAnswer ? "bg-orange-500" : "bg-gray-300"}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${exitOnNoAnswer ? "left-4" : "left-0.5"}`} />
              </button>
            </div>
            <p className="text-[9px] text-gray-400 -mt-1.5">
              End the KB session early and move to the next node if the knowledge base can't answer a question.
            </p>
          </div>
        )}

        {/* Message History Context (available in both modes) */}
        <div>
          <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">
            <History size={10} /> Context: Last Messages
          </label>
          <input
            type="number"
            min={0}
            max={50}
            value={prePromptMessagesCount}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              setPrePromptMessagesCount(isNaN(val) || val < 0 ? 0 : val);
            }}
            className="w-full px-2.5 py-1.5 text-[11px] font-semibold border border-gray-200 rounded-lg focus:ring-1 focus:ring-purple-400 focus:border-purple-400 focus:outline-none"
            placeholder="0"
          />
          <p className="text-[9px] text-gray-400 mt-0.5">
            How many recent conversation messages to include as context when answering. Set to 0 to use only the current question.
          </p>
        </div>

        {/* Context Variables (multi-select) */}
        <div>
          <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">
            <Variable size={10} /> Context Variables
          </label>
          {flowVariables.length === 0 ? (
            <p className="text-[9px] text-gray-400 italic">No variables available.</p>
          ) : (
            <div className="max-h-28 overflow-y-auto space-y-1">
              {flowVariables.map((v) => (
                <label
                  key={v.id || v.name}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
                    contextVariableNames.includes(v.name) ? "bg-[#E7FCE8]" : "hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={contextVariableNames.includes(v.name)}
                    onChange={() => toggleVariable(v.name)}
                    className="w-3 h-3 rounded border-gray-300 text-[#008069] focus:ring-[#008069]"
                  />
                  <span className="text-[10px] font-mono font-semibold text-[#008069]">{v.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Send reply toggle */}
        <div className="flex items-center justify-between px-2 py-2 bg-gray-50 rounded-lg">
          <span className="text-[10px] font-bold text-gray-600 flex items-center gap-1">
            <Send size={10} /> Send reply to user
          </span>
          <button
            onClick={() => setSendReply(!sendReply)}
            className={`w-9 h-5 rounded-full transition-colors relative ${sendReply ? "bg-[#008069]" : "bg-gray-300"}`}
          >
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${sendReply ? "left-4" : "left-0.5"}`} />
          </button>
        </div>

        {/* Store in variable */}
        <div>
          <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">
            <Variable size={10} /> Store answer in variable (optional)
          </label>
          <VariableDropdown
            value={storeInVariable}
            onChange={(name) => setStoreInVariable(name)}
            flowVariables={flowVariables}
            allowCreate
            onCreateVariable={onCreateVariable}
            placeholder="Select variable to store answer..."
          />
        </div>
      </div>

      </NodeEditorTabs>
      <div className={shell.footerClass}>
        <button
          onClick={onCancel}
          className={`${fs ? "px-5 py-2.5 text-sm" : "flex-1 py-2 text-[12px]"} font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200`}
        >
          Cancel
        </button>
        <button
          onClick={() => onSave(buildSavePayload())}
          className={`${fs ? "px-5 py-2.5 text-sm" : "flex-1 py-2 text-[12px]"} font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm`}
        >
          <Check size={14} /> Save changes
        </button>
      </div>
    </div>
  );
};

// ─── Main AIKnowledgeBaseAnswerNode ────────────────────
const AIKnowledgeBaseAnswerNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("aiKnowledgeBaseAnswer");
  const intents = data.intents || [];
  const sessionMode = data.sessionMode || false;
  const hasIntents = intents.length > 0;
  // Hub mode: intents route out; FAQ stays on node — hide unused "answer" exit dot
  const showAnswerExitHandle = sessionMode && !hasIntents;
  const showNoAnswerHandle =
    data.showNoAnswerHandle !== false &&
    (hasIntents || sessionMode || data.exitOnNoAnswer);
  const showSessionHandles =
    hasIntents || showAnswerExitHandle || showNoAnswerHandle;

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...data, ...formData });
      }
      closeEditor();
    },
    [id, data, closeEditor]);

  return (
    <div
      className={`relative transition-all shadow-md`}
      style={{ borderRadius: "12px" }}
    >
      <NodeActionButtons id={id} data={data} />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-purple-500 !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />

      <CollapsedView
          data={data}
          onEdit={openEditor}
          intents={intents}
          showNoAnswerHandle={showNoAnswerHandle}
        />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          presentation="fullscreen"
          onCancel={closeEditor}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
        />
      </FlowNodeEditorShell>

      {/* Single-shot exit handles (no intents) */}
      {showAnswerExitHandle && (
        <Handle
          type="source"
          position={Position.Right}
          id="answer"
          className="!w-3.5 !h-3.5 !bg-emerald-500 !border-2 !border-white !shadow-sm"
          style={{ right: -7, top: "50%" }}
          title="Session done (answered)"
        />
      )}

      {showNoAnswerHandle && !hasIntents && (
        <Handle
          type="source"
          position={Position.Right}
          id="no_answer"
          className="!w-3.5 !h-3.5 !bg-orange-500 !border-2 !border-white !shadow-sm"
          style={{ right: -7, top: "70%" }}
          title="KB can't answer → route here"
        />
      )}

      {!showSessionHandles && intents.length === 0 && (
        <Handle
          type="source"
          position={Position.Right}
          className="!w-3.5 !h-3.5 !bg-purple-500 !border-2 !border-white !shadow-sm"
          style={{ right: -7 }}
        />
      )}
    </div>
  );
};

export default AIKnowledgeBaseAnswerNode;
